"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Sparkles, ArrowRight, Wand2, Wrench, Bug, Check } from "lucide-react"
import { CURRENT_RELEASE, type ReleaseEntryKind } from "@/data/release-notes"

/** Stores the acknowledged version, so a future release re-opens the modal. */
const STORAGE_KEY = "satbuddy-whats-new-acknowledged"
/** Set by the first-visit onboarding modal. Its presence means the user is
 *  an existing user, not someone seeing the dashboard for the first time. */
const ONBOARDING_KEY = "access-mode-onboarding-shown"

const KIND_STYLE: Record<
    ReleaseEntryKind,
    { icon: React.ElementType; label: string; chip: string }
> = {
    feature: {
        icon: Wand2,
        label: "New",
        chip: "bg-primary/10 text-primary border-primary/20",
    },
    improvement: {
        icon: Wrench,
        label: "Improved",
        chip: "bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20",
    },
    fix: {
        icon: Bug,
        label: "Fixed",
        chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    },
}

/** True once the user has acknowledged the current release. Lets the home page
 *  hold back other modals so two don't fight over the screen. */
export function hasAcknowledgedCurrentRelease(): boolean {
    try {
        return (
            localStorage.getItem(STORAGE_KEY) === CURRENT_RELEASE.version ||
            // First-time users never see the modal, so nothing is blocking.
            !localStorage.getItem(ONBOARDING_KEY)
        )
    } catch {
        return true
    }
}

export function WhatsNewModal({ onAcknowledge }: { onAcknowledge?: () => void }) {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        try {
            if (localStorage.getItem(STORAGE_KEY) === CURRENT_RELEASE.version) return

            // A first-time user already gets the onboarding modal and the tour;
            // stacking a changelog on top of that is noise, and "what's new" means
            // nothing to someone for whom everything is new. Mark it acknowledged
            // so it never surfaces retroactively either.
            if (!localStorage.getItem(ONBOARDING_KEY)) {
                localStorage.setItem(STORAGE_KEY, CURRENT_RELEASE.version)
                onAcknowledge?.()
                return
            }

            setOpen(true)
        } catch {
            // Private browsing with storage disabled — just don't nag.
        }
        // onAcknowledge is a stable callback from the page; re-running on identity
        // change would risk reopening the dialog.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Only an explicit acknowledgement dismisses this for good. */
    const acknowledge = () => {
        try {
            localStorage.setItem(STORAGE_KEY, CURRENT_RELEASE.version)
        } catch {
            // Storage unavailable; the modal simply returns next visit.
        }
        setOpen(false)
        onAcknowledge?.()
    }

    return (
        <Dialog open={open} onOpenChange={(next) => next && setOpen(true)}>
            <DialogContent
                showCloseButton={false}
                // Escape and click-away are disabled on purpose: the user asked
                // for this to stay put until it is acknowledged.
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                className="max-h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-lg"
            >
                {/* Header */}
                <DialogHeader className="relative space-y-0 overflow-hidden border-b bg-gradient-to-br from-primary/12 via-primary/5 to-transparent p-6 text-left">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full bg-primary/15 blur-3xl"
                    />
                    <div className="relative flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-background/70 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary backdrop-blur">
                            <Sparkles className="h-3 w-3" />
                            What&apos;s new
                        </span>
                        <span className="text-xs font-medium text-muted-foreground">
                            {CURRENT_RELEASE.date}
                        </span>
                    </div>
                    <DialogTitle className="relative mt-3 text-2xl font-extrabold tracking-tight">
                        {CURRENT_RELEASE.headline.title}
                    </DialogTitle>
                    <DialogDescription className="relative mt-2 text-sm leading-relaxed">
                        {CURRENT_RELEASE.headline.description}
                    </DialogDescription>

                    {CURRENT_RELEASE.headline.href && (
                        <Link
                            href={CURRENT_RELEASE.headline.href}
                            onClick={acknowledge}
                            className="relative mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-all hover:brightness-110 active:scale-95"
                        >
                            {CURRENT_RELEASE.headline.cta}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    )}
                </DialogHeader>

                {/* Entries */}
                <div className="max-h-[42vh] overflow-y-auto px-6 py-5">
                    <ul className="space-y-4">
                        {CURRENT_RELEASE.entries.map((entry) => {
                            const style = KIND_STYLE[entry.kind]
                            const Icon = style.icon
                            return (
                                <li key={entry.title} className="flex gap-3">
                                    <div
                                        className={cn(
                                            "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border",
                                            style.chip
                                        )}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <h3 className="text-sm font-bold leading-snug">
                                                {entry.title}
                                            </h3>
                                            <span
                                                className={cn(
                                                    "shrink-0 rounded-full border px-1.5 py-px text-[10px] font-bold uppercase tracking-wide",
                                                    style.chip
                                                )}
                                            >
                                                {style.label}
                                            </span>
                                        </div>
                                        <p className="mt-1 break-words text-sm leading-relaxed text-muted-foreground">
                                            {entry.description}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>

                {/* Acknowledge */}
                <div className="border-t bg-muted/20 p-4">
                    <Button
                        onClick={acknowledge}
                        className="h-11 w-full gap-2 rounded-xl text-sm font-bold shadow-sm"
                    >
                        <Check className="h-4 w-4" />
                        Got it
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
